import type { Account } from '@/features/accounts/accounts.api';

/**
 * A per-account identity color: a solid swatch for the sidebar row and a
 * tinted badge (background + text + border) for the aggregate-mode
 * conversation-row badge.
 *
 * Every class string below is a LITERAL. Tailwind's JIT scanner only picks
 * up complete class names it can find as substrings of source files — a
 * dynamically-assembled name like `` `bg-${hue}-500` `` would never be
 * generated. Keeping the whole palette as literal object properties (rather
 * than building class names from a color-name + shade pair at runtime)
 * sidesteps that entirely.
 */
export interface AccountColor {
  /** Solid swatch square in the sidebar. */
  swatch: string;
  /** Badge TEXT color on aggregate conversation rows. Since the neumorphic
   *  redesign the badge chrome (inset micro-pill: padding, radius, shadow)
   *  lives in `ConversationRow`'s badge node; the palette only contributes
   *  the account's identity color as text color per the design handoff. */
  badge: string;
}

/**
 * Fixed 8-entry color palette, cycled by account index — see
 * `buildAccountColorMap`. Eight keeps the current fleet (2 live accounts,
 * more coming) fully distinct for a good while before any two accounts
 * share a color.
 */
export const ACCOUNT_COLOR_PALETTE: readonly AccountColor[] = [
  { swatch: 'bg-blue-500',    badge: 'text-blue-600 dark:text-blue-400' },
  { swatch: 'bg-violet-500',  badge: 'text-violet-600 dark:text-violet-400' },
  { swatch: 'bg-orange-500',  badge: 'text-orange-600 dark:text-orange-400' },
  { swatch: 'bg-emerald-500', badge: 'text-emerald-600 dark:text-emerald-400' },
  { swatch: 'bg-rose-500',    badge: 'text-rose-600 dark:text-rose-400' },
  { swatch: 'bg-amber-500',   badge: 'text-amber-600 dark:text-amber-500' },
  { swatch: 'bg-indigo-500',  badge: 'text-indigo-600 dark:text-indigo-400' },
  { swatch: 'bg-fuchsia-500', badge: 'text-fuchsia-600 dark:text-fuchsia-400' },
];

/**
 * Assigns a stable color to every account, keyed by account id.
 *
 * Assignment is by INDEX in `accounts` — the order `GET /accounts` returns
 * (`created_at` ascending) — not a hash of the account id. `/accounts` is
 * append-only (existing rows are never reordered or removed from the
 * list), so a given account's index — and therefore its color — never
 * shifts as new accounts are added later. That stability is worth more
 * than a hash's better worst-case distribution: with a hash, adding a new
 * account could (by chance) collide with an existing account's color and
 * an agent would see two accounts sharing a color for no reason visible in
 * the UI. Index-based assignment also guarantees the first 8 accounts —
 * the realistic fleet size for a while yet — are all pairwise distinct.
 */
export function buildAccountColorMap(
  accounts: readonly Account[],
): Map<string, AccountColor> {
  const map = new Map<string, AccountColor>();
  accounts.forEach((account, index) => {
    map.set(account.id, ACCOUNT_COLOR_PALETTE[index % ACCOUNT_COLOR_PALETTE.length]);
  });
  return map;
}
